/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { useCallback, useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { CopyPlusIcon, FileIcon, PlusIcon, Trash2Icon } from "lucide-react";
import {
  FieldPath,
  useFieldArray,
  useForm,
  UseFormReturn,
} from "react-hook-form";
import { z } from "zod";

import {
  ConfirmationDialog,
  DisabledExplanationTooltip,
  EmptyState,
  KeyboardShortcut,
  Row,
  StickyTable,
  TableCellCurrencyInput,
  TableCellDatePicker,
  TableCellEnumMultiselect,
  TableCellEnumSelect,
  TableCellNumberInput,
  TableCellNumberMultiselect,
  TableCellTextInput,
} from "@/components";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { mergeUserOptionsWithSelected, useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useCreateRequisition } from "@/hooks/mutations/useCreateRequisition";
import { useCreateRequisitionOwner } from "@/hooks/mutations/useCreateRequisitionOwner";
import { useDeleteRequisition } from "@/hooks/mutations/useDeleteRequisition";
import { useDeleteRequisitionOwner } from "@/hooks/mutations/useDeleteRequisitionOwner";
import { useDuplicateRequisition } from "@/hooks/mutations/useDuplicateRequisition";
import { useUpdateRequisition } from "@/hooks/mutations/useUpdateRequisition";
import { useGetRequisitions } from "@/hooks/queries/useGetRequisitions";
import { useSetRequisition } from "@/hooks/sets/useSetRequisition";
import {
  requisitionStatusOptions,
  requisitionStatusToDotColor,
  shiftTypeOptions,
  shiftTypeToLabel,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { misc } from "@/lib/misc";
import { nullableDateSchema } from "@/lib/schemas";
import {
  CompensationType,
  Job,
  Requisition,
  RequisitionQueryParams,
  RequisitionStatus,
  ShiftType,
} from "@/types";

const DISABLED_REQUISITION_STATUS_VALUES = [
  RequisitionStatus.BACKFILL,
  RequisitionStatus.COMPLETED,
  RequisitionStatus.FILLED,
];

// * IMPORTANT: These schema names need to stay consistent with the column names in the requisition table.
// * IMPORTANT: We are indexing the requisition object by field name of the form to compare values in handleOnBlur() and sending fieldName directly to the BE for updates.
const requisitionSchema = z.object({
  id: z.number(),
  externalReqId: z.string().nullable(),
  dateDue: nullableDateSchema,
  dateFilled: nullableDateSchema,
  dateLost: nullableDateSchema,
  dateProjectedClose: nullableDateSchema,
  dateRequested: nullableDateSchema,
  dateStarted: nullableDateSchema,
  retentionBonusDays: z.coerce.number().min(0).nullable(),
  lostReason: z.string().nullable(),
  requisitionOwners: z.array(z.coerce.number()),
  retentionBonusAmount: z.coerce.number().min(0).nullable(),
  shiftDifferentialMin: z.coerce.number().min(0).nullable(),
  shiftDifferentialMax: z.coerce.number().min(0).nullable(),
  shiftType: z.array(
    z.nativeEnum(ShiftType, {
      required_error: "Shift type is required",
    }),
  ),
  shiftTypeDescription: z.string().nullable(),
  status: z.nativeEnum(RequisitionStatus, {
    required_error: "Status is required",
  }),
});

const schema = z.object({
  requisitions: z.array(requisitionSchema),
});

const showInlineError = (
  form: UseFormReturn<z.infer<typeof schema>>,
  fieldName: FieldPath<z.infer<typeof schema>>,
) => {
  return form.getFieldState(fieldName).error ? "bg-red-100" : "";
};

// Format dates for display
const requisitionToSchema = (requisition: Requisition) => {
  return {
    ...requisition,
    dateDue: dates.formatIsoDateForUser(requisition.dateDue),
    dateFilled: dates.formatIsoDateForUser(requisition.dateFilled),
    dateLost: dates.formatIsoDateForUser(requisition.dateLost),
    dateProjectedClose: dates.formatIsoDateForUser(
      requisition.dateProjectedClose,
    ),
    dateRequested: dates.formatIsoDateForUser(requisition.dateRequested),
    dateStarted: dates.formatIsoDateForUser(requisition.dateStarted),
    requisitionOwners: requisition.requisitionOwners.map(
      (reqOwner) => reqOwner.userId,
    ),
  };
};

export const RequisitionsTable = ({
  job,
  requisitionQueryParams,
}: {
  job: Job;
  requisitionQueryParams: RequisitionQueryParams;
}) => {
  /* Contexts */
  const { userNameMap, userOptions: activeUserOptions } = useTaggableUsers();

  /* UseStates */
  const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] =
    useState(false);
  const [requisitionToDelete, setRequisitionToDelete] = useState<z.infer<
    typeof requisitionSchema
  > | null>(null);

  /* Database CRUD Operations */
  // Create
  const { mutate: createRequisition } = useCreateRequisition();
  const { mutate: createRequisitionOwner } = useCreateRequisitionOwner();
  const { mutate: duplicateRequisition } = useDuplicateRequisition();

  // Reads
  const { data: requisitions, status: requisitionsStatus } = useGetRequisitions(
    {
      jobId: job.id,
      queryOptions: requisitionQueryParams,
    },
  );

  // Update
  const { mutate: updateRequisition } = useUpdateRequisition();
  const setRequisition = useSetRequisition();

  // Delete
  const { mutate: deleteRequisition } = useDeleteRequisition();
  const { mutate: deleteRequisitionOwner } = useDeleteRequisitionOwner();

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { requisitions: [] },
  });
  const { fields } = useFieldArray({
    control: form.control,
    name: "requisitions",
    // Set React Hook Form's generated key to "requisitionId" instead of defaulted "id" so Laborup "id" column can be referenced from the form for updates/deletes
    keyName: "requisitionId",
  });

  const watchedRequisitions = form.watch("requisitions");

  const handleAddRequisition = useCallback(() => {
    const newReq = {
      shiftDifferentialMin: job.shiftDifferentialMin,
      shiftDifferentialMax: job.shiftDifferentialMax,
      shiftType: job.shiftType,
      status: RequisitionStatus.PENDING,
    };

    createRequisition(
      {
        createRequisitionRequest: newReq,
      },
      {
        onSuccess: (createdRequisition) => {
          form.setValue("requisitions", [
            ...form.getValues("requisitions"),
            requisitionToSchema(createdRequisition),
          ]);

          // TODO: Get this working with form.setFocus()
          // Focus on the new row, specifically the status column, which is the first column after ID
          setTimeout(() => {
            const cell = document.getElementById(
              `cell-status-${fields.length}`,
            );
            cell?.focus();
          }, 0);
        },
      },
    );
  }, [createRequisition, fields, form, job]);

  // Populate form with the requisitions data
  useEffect(() => {
    if (!requisitions) return;
    // TODO: STF-168 - Figure out why this is needed here but not in TasksTable.tsx
    // Don't want the form to reset if a field within a requisition is updated. It doesn't allow focus to shift from one field (updated) to another. Rerender loses the focus.
    if (requisitions.length !== form.getValues("requisitions").length) {
      form.reset({
        requisitions: requisitions.map((r) => requisitionToSchema(r)),
      });
    }
  }, [form, requisitions]);

  // Shift + Enter adds a new requisition
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault();
        e.stopPropagation(); // prevent dropdowns that are focused from opening before new row is added, which caused a glitchy UI if somebody adds say 3 reqs back-to-back-to-back.
        mixpanelUtils.trackKeyboardShortcutUsed(
          "Shift+Enter (Add Requisition)",
          MIXPANEL_SCREENS.JOB,
        );
        handleAddRequisition();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [handleAddRequisition]);

  if (requisitionsStatus === "pending") {
    // TODO: Loading state.
    return null;
  } else if (requisitionsStatus === "error") {
    // TODO: Error state.
    return null;
  }

  const handleOnBlur = (
    id: number,
    fieldName: string,
    updatedValue: string | null,
    type?: "date" | "number",
  ) => {
    const valueToUpdate = misc.handleFormFieldToDatabaseValue(
      updatedValue,
      type,
    );
    updateRequisition(
      {
        id,
        updateRequisitionRequest: { [fieldName]: valueToUpdate },
      },
      {
        onSuccess: (updatedRequisition) => {
          setRequisition({ updatedRequisition, requisitionQueryParams });
        },
      },
    );
  };

  const handleDeleteRequisition = (index: number) => {
    const requisition = form.getValues(`requisitions.${index}`);
    setRequisitionToDelete(requisition);
    setIsDeleteConfirmationDialogOpen(true);
  };

  const handleDeleteRequisitionConfirmation = () => {
    if (!requisitionToDelete) return;
    deleteRequisition(
      { requisitionId: requisitionToDelete.id },
      {
        onSuccess: (deletedRequisition) => {
          setIsDeleteConfirmationDialogOpen(false);
          setRequisitionToDelete(null);
          form.setValue(
            "requisitions",
            form
              .getValues("requisitions")
              .filter((r) => r.id !== deletedRequisition.id),
          );
        },
      },
    );
  };

  const handleDuplicateRequisition = (index: number) => {
    const requisition = form.getValues(`requisitions.${index}`);

    duplicateRequisition(
      { requisitionId: requisition.id },
      {
        onSuccess: (duplicatedRequisition) => {
          form.setValue("requisitions", [
            ...form.getValues("requisitions"),
            requisitionToSchema(duplicatedRequisition),
          ]);
        },
      },
    );
  };

  const listName = "requisitions";

  const requisitionStatuses = requisitionStatusOptions.map((option) => ({
    ...option,
    disabled: DISABLED_REQUISITION_STATUS_VALUES.includes(option.value),
  }));

  return (
    <Form {...form}>
      <form>
        <StickyTable
          hasRows={fields.length > 0}
          footer={
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                mixpanelUtils.trackButtonClick(
                  "Add Requisition",
                  MIXPANEL_SCREENS.JOB,
                );
                handleAddRequisition();
              }}
            >
              <PlusIcon />
              Add Requisition
              <KeyboardShortcut text="⇧↵" />
            </Button>
          }
        >
          <TableHeader className="sticky top-0 z-10 bg-slate-50 [&_tr]:border-b-0">
            <TableRow className="sticky-border-bottom">
              <TableHead>Req ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Date Requested</TableHead>
              <TableHead>Date Started</TableHead>
              <TableHead>Date Due</TableHead>
              <TableHead>Projected Close Date</TableHead>
              <TableHead>Shift Type</TableHead>
              <TableHead>Shift Type Description</TableHead>
              <TableHead>Shift Differential Min</TableHead>
              <TableHead>Shift Differential Max</TableHead>
              <TableHead>Retention Bonus Days</TableHead>
              <TableHead>Retention Bonus Amount</TableHead>
              <TableHead>Date Filled</TableHead>
              <TableHead>Date Lost</TableHead>
              <TableHead>Lost Reason</TableHead>
              <TableHead className="sticky-border-left sticky right-0 bg-slate-50">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.length > 0 ? (
              fields.map((row, index) => {
                const placementCount = requisitions.find((r) => r.id === row.id)
                  ?.placements.length;
                const rowOwnerOptions = mergeUserOptionsWithSelected(
                  activeUserOptions,
                  userNameMap,
                  watchedRequisitions[index]?.requisitionOwners ??
                    row.requisitionOwners,
                );
                return (
                  <TableRow key={row.requisitionId} className="hover:shadow-sm">
                    <TableCell className="whitespace-nowrap">
                      {row.externalReqId}
                    </TableCell>
                    <TableCellEnumSelect
                      className="w-36"
                      fieldName="status"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      options={requisitionStatuses}
                      rowId={row.id}
                      statusToDotColor={requisitionStatusToDotColor}
                    />
                    <TableCellNumberMultiselect
                      fieldName="requisitionOwners"
                      form={form}
                      handleOnSelect={(
                        updatedValue: number,
                        isSelected: boolean,
                      ) => {
                        if (isSelected) {
                          deleteRequisitionOwner({
                            deleteRequisitionOwnerRequest: {
                              requisitionId: row.id,
                              userId: updatedValue,
                            },
                          });
                        } else {
                          createRequisitionOwner({
                            createRequisitionOwnerRequest: {
                              requisitionId: row.id,
                              userId: updatedValue,
                            },
                          });
                        }
                      }}
                      index={index}
                      listName={listName}
                      numberToLabel={userNameMap}
                      options={rowOwnerOptions}
                    />
                    <TableCellDatePicker
                      fieldName="dateRequested"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={row.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellDatePicker
                      fieldName="dateStarted"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={row.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellDatePicker
                      fieldName="dateDue"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={row.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellDatePicker
                      fieldName="dateProjectedClose"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={row.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellEnumMultiselect<z.infer<typeof schema>, ShiftType>
                      enumToLabel={shiftTypeToLabel}
                      fieldName="shiftType"
                      form={form}
                      handleOnSelect={(updatedValue: ShiftType[]) => {
                        updateRequisition(
                          {
                            id: row.id,
                            updateRequisitionRequest: {
                              shiftType: updatedValue,
                            },
                          },
                          {
                            onSuccess: (updatedRequisition) => {
                              setRequisition({
                                updatedRequisition,
                                requisitionQueryParams,
                              });
                            },
                          },
                        );
                      }}
                      index={index}
                      listName={listName}
                      options={shiftTypeOptions}
                    />
                    <TableCellTextInput
                      fieldName="shiftTypeDescription"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={row.id}
                    />
                    <TableCellCurrencyInput
                      disabled={
                        job.compensationType === CompensationType.YEARLY
                      }
                      fieldName="shiftDifferentialMin"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={row.id}
                    />
                    <TableCellCurrencyInput
                      disabled={
                        job.compensationType === CompensationType.YEARLY
                      }
                      fieldName="shiftDifferentialMax"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={row.id}
                    />
                    <TableCellNumberInput
                      fieldName="retentionBonusDays"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={row.id}
                    />
                    <TableCellCurrencyInput
                      fieldName="retentionBonusAmount"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={row.id}
                    />
                    <TableCellDatePicker
                      disabled
                      fieldName="dateFilled"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={row.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellDatePicker
                      fieldName="dateLost"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={row.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellTextInput
                      fieldName="lostReason"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={row.id}
                    />
                    <TableCell className="sticky-border-left sticky right-0 bg-slate-50">
                      <Row className="justify-center gap-4">
                        <Button
                          disabled={
                            ![
                              RequisitionStatus.PENDING,
                              RequisitionStatus.LIVE,
                            ].includes(row.status)
                          }
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            handleDuplicateRequisition(index);
                          }}
                          className="h-4 w-4"
                        >
                          <CopyPlusIcon />
                        </Button>
                        <DisabledExplanationTooltip
                          disabled={!!placementCount}
                          message="Can't delete requisitions with placements"
                          side="left"
                        >
                          <Button
                            disabled={!!placementCount}
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              handleDeleteRequisition(index);
                            }}
                            className="h-4 w-4"
                          >
                            <Trash2Icon />
                          </Button>
                        </DisabledExplanationTooltip>
                      </Row>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell className="p-0" colSpan={25}>
                  <EmptyState
                    className="bg-slate-50 py-14"
                    description={`No requisitions were found. ${requisitionQueryParams.statuses.length > 0 ? `Try clearing the filters to see requisitions for this job.` : `Add a requisition to get started!`}`}
                    Icon={FileIcon}
                    primaryButtonName="Add Requisition"
                    PrimaryButtonIcon={PlusIcon}
                    handlePrimaryClick={() => {
                      mixpanelUtils.trackButtonClick(
                        "Add Requisition",
                        MIXPANEL_SCREENS.JOB,
                      );
                      handleAddRequisition();
                    }}
                    title="No requisitions found"
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StickyTable>
        <ConfirmationDialog
          confirmationButtonVariant="destructive"
          confirmButtonText="Delete"
          description={`Are you sure you want to delete requisition ${requisitionToDelete?.id}? ${requisitionToDelete?.dateRequested ? `The requisition was requested by the client on ${requisitionToDelete.dateRequested}.` : ""} ${
            requisitionToDelete?.requisitionOwners &&
            requisitionToDelete.requisitionOwners.length > 0
              ? `It is owned by ${requisitionToDelete.requisitionOwners
                  .map((owner) => userNameMap[owner])
                  .join(", ")}.`
              : ""
          } This action cannot be undone.`}
          onConfirm={handleDeleteRequisitionConfirmation}
          onOpenChange={setIsDeleteConfirmationDialogOpen}
          open={isDeleteConfirmationDialogOpen}
          title="Delete Requisition"
        />
      </form>
    </Form>
  );
};
